import { use, useRef, useState } from "react"
import cx from "classix"
import { MaterialIcon } from "~/shared/ui"
import type { CheckboxProps } from "./CheckboxProps"
import { Ripple } from "../Ripple"
import { getRippleColorFromColorStyle } from "../../utils"

import indexCss from "./index.module.css"
import { inv } from "~/shared/lib/utils"
import { FieldsetContext } from "~/shared/ui/kit/components/Fieldset"

const rippleStyle = {
	width: "200%",
	height: "200%",
	left: "-50%",
	top: "-50%",
	borderRadius: "100%"
}

export type { CheckboxProps }

/**
 * Обертка вокруг `<input type='checkbox'>`.
 */
export const Checkbox = function Checkbox({
											  name,
											  colorStyle = "primary",
											  disabled = false,
											  checked,
											  onClick, onChange,
											  children,
											  value,
											  defaultChecked = false,
											  className,
											  ...props
										  }: CheckboxProps) {

	const fs = use(FieldsetContext)
	const isDisabled = disabled || fs.disabled

	const inputRef = useRef<HTMLInputElement>(null)

	const [internalChecked, setInternalChecked] = useState(defaultChecked)

	const isControlled = checked !== undefined
	const currentChecked = isControlled ? checked : internalChecked

	const wasControlled = useRef(checked !== undefined)

	const handleClick = () => {
		inputRef.current?.focus()

		if (!isControlled) {
			setInternalChecked(inv)
		}

		onClick?.()
		onChange?.(!currentChecked)
	}

	if (import.meta.env.DEV) {
		if (wasControlled.current !== isControlled) {
			if (wasControlled.current) {
				throw new Error("Компонент был контролируемым, а стал неконтролируемым")
			} else {
				throw new Error("Компонент был неконтролируемым, а стал контролируемым")
			}
		}
	}

	return (
		<div role="checkbox"
			 aria-checked={currentChecked}
			 tabIndex={0}
			 onClick={handleClick}
			 className={cx(indexCss.checkbox, isDisabled && indexCss.disabled, className)} {...props} >
			<input ref={inputRef} name={name} type="checkbox" readOnly checked={currentChecked} value={value} />
			<div className={cx(`cs-${colorStyle}`, indexCss.checkboxIcon, currentChecked && indexCss.checked)}>
				<Ripple style={rippleStyle} color={getRippleColorFromColorStyle(colorStyle)} />
				{currentChecked && <MaterialIcon name="check" />}
			</div>

			<div className={indexCss.label}>{children}</div>
		</div>
	)
}

Checkbox.displayName = "Checkbox"